'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProcessedMainMenuItem } from '@/components/menu/main-menu';
import { useFittingItemCount } from '@/components/menu/use-fitting-item-count';
import { LinkComponent } from '@/components/ui/link-component';
import { languageOptions } from '@/config/language-options';
import type { Locale, StaticTranslationString } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { ChevronDown, Languages, Search, X } from 'lucide-react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';
const overflowButtonLabel: StaticTranslationString = {
  de: 'Mehr',
  fr: 'Plus',
  en: 'More'
};
const searchLabel: StaticTranslationString = {
  de: 'Suchen',
  fr: 'Rechercher',
  en: 'Search'
};
const searchPlaceholder: StaticTranslationString = {
  de: 'Suchen...',
  fr: 'Rechercher...',
  en: 'Search...'
};

/** The `openDropdownId` of the overflow flyout, which lists the items that did not fit. */
const OVERFLOW_DROPDOWN_ID = 'overflow';
const topLevelItemClassName = 'flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-semibold whitespace-nowrap';
const topLevelRowClassName = 'flex items-center gap-1 xl:gap-1.5';
const hasSubMenu = (item: ProcessedMainMenuItem): boolean => Array.isArray(item.subMenu) && item.subMenu.length > 0;
const DropdownButton: React.FC<{
  label: string;
  isOpen: boolean;
  onToggle: () => void;
}> = t0 => {
  const $ = _c(14);
  const {
    label,
    isOpen,
    onToggle
  } = t0;
  const t1 = isOpen ? "bg-conveniat-green/10 text-conveniat-green" : "hover:bg-conveniat-green/10 hover:text-conveniat-green text-gray-700";
  let t2;
  if ($[0] !== t1) {
    t2 = cn(topLevelItemClassName, "transition-all duration-200", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <span>{label}</span>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isOpen && "text-conveniat-green rotate-180";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("size-4 text-gray-400 transition-transform duration-200", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <ChevronDown className={t5} />;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isOpen || $[9] !== onToggle || $[10] !== t2 || $[11] !== t3 || $[12] !== t6) {
    t7 = <button type="button" aria-expanded={isOpen} onClick={onToggle} className={t2}>{t3}{t6}</button>;
    $[8] = isOpen;
    $[9] = onToggle;
    $[10] = t2;
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};
const TopLevelItem: React.FC<{
  item: ProcessedMainMenuItem;
  isOpen: boolean;
  onToggle: () => void;
}> = t0 => {
  const $ = _c(12);
  const {
    item,
    isOpen,
    onToggle
  } = t0;
  if (hasSubMenu(item)) {
    let t1;
    if ($[0] !== isOpen || $[1] !== item.label || $[2] !== onToggle) {
      t1 = <DropdownButton label={item.label} isOpen={isOpen} onToggle={onToggle} />;
      $[0] = isOpen;
      $[1] = item.label;
      $[2] = onToggle;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  if (typeof item.itemLink === "string" && item.itemLink !== "") {
    const t1 = item.itemLink;
    const t2 = item.openInNewTab;
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = cn(topLevelItemClassName, "hover:bg-conveniat-green/10 hover:text-conveniat-green text-gray-700 transition-all duration-200");
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== item.itemLink || $[6] !== item.label || $[7] !== item.openInNewTab) {
      t4 = <LinkComponent href={t1} openInNewTab={t2} prefetch={true} className={t3}>{item.label}</LinkComponent>;
      $[5] = item.itemLink;
      $[6] = item.label;
      $[7] = item.openInNewTab;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  let t1;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(topLevelItemClassName, "text-gray-400");
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== item.label) {
    t2 = <span className={t1}>{item.label}</span>;
    $[10] = item.label;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
const SubItemWithoutChildren: React.FC<{
  subItem: ProcessedMainMenuItem;
}> = t0 => {
  const $ = _c(9);
  const {
    subItem
  } = t0;
  if (typeof subItem.itemLink === "string" && subItem.itemLink !== "") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="bg-conveniat-green/40 group-hover/item:bg-conveniat-green size-1.5 rounded-full transition-colors" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== subItem.label) {
      t2 = <span className="flex items-center gap-2">{t1}<span>{subItem.label}</span></span>;
      $[1] = subItem.label;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== subItem.itemLink || $[4] !== subItem.openInNewTab || $[5] !== t2) {
      t3 = <LinkComponent href={subItem.itemLink} openInNewTab={subItem.openInNewTab} prefetch={true} className="group/item hover:bg-conveniat-green/10 hover:text-conveniat-green flex items-center rounded-xl px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-150">{t2}</LinkComponent>;
      $[3] = subItem.itemLink;
      $[4] = subItem.openInNewTab;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t1;
  if ($[7] !== subItem.label) {
    t1 = <span className="block px-3 py-2 text-sm font-medium text-gray-400">{subItem.label}</span>;
    $[7] = subItem.label;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
};

/**
 * Calls `onDismiss` on a pointer press outside every referenced element, or on Escape, while `isOpen`.
 */
const useDismissOnOutsideInteraction = (references, isOpen, onDismiss) => {
  const $ = _c(5);
  let t0;
  let t1;
  if ($[0] !== isOpen || $[1] !== onDismiss || $[2] !== references) {
    t0 = () => {
      if (!isOpen) {
        return;
      }
      const handlePointerDown = event => {
        const target = event.target;
        if (!(target instanceof Node)) {
          return;
        }
        if (references.some(reference => reference.current?.contains(target) === true)) {
          return;
        }
        onDismiss();
      };
      const handleKeyDown = event_0 => {
        if (event_0.key === "Escape") {
          onDismiss();
        }
      };
      document.addEventListener("pointerdown", handlePointerDown);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("pointerdown", handlePointerDown);
        document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t1 = [references, isOpen, onDismiss];
    $[0] = isOpen;
    $[1] = onDismiss;
    $[2] = references;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
};
export const DesktopNav: React.FC<{
  locale: Locale;
  menuItems: ProcessedMainMenuItem[];
  actionURL: string;
}> = t0 => {
  const $ = _c(65);
  const {
    locale,
    menuItems,
    actionURL
  } = t0;
  const [openDropdownId, setOpenDropdownId] = useState();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navReference = useRef(null);
  const measureRowReference = useRef(null);
  const flyoutReference = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [navReference, flyoutReference];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const dismissReferences = t1;
  const searchInputReference = useRef(null);
  const router = useRouter();
  const pathname = usePathname();
  const searchParameters = useSearchParams();
  const navItems = menuItems.filter(_temp);
  const fittingCount = useFittingItemCount(navReference, measureRowReference, navItems.length);
  const fittingItems = navItems.slice(0, fittingCount);
  const overflowItems = navItems.slice(fittingCount);
  let t2;
  if ($[1] !== openDropdownId) {
    t2 = item_0 => item_0.id === openDropdownId;
    $[1] = openDropdownId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const activeItem = menuItems.find(t2);
  const activeSubMenu = activeItem?.subMenu;
  const hasActiveSubMenu = Array.isArray(activeSubMenu) && activeSubMenu.length > 0;
  const isOverflowOpen = openDropdownId === OVERFLOW_DROPDOWN_ID || overflowItems.some(item_1 => item_1.id === openDropdownId);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setOpenDropdownId(undefined);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const closeDropdown = t3;
  useDismissOnOutsideInteraction(dismissReferences, hasActiveSubMenu || isOverflowOpen, closeDropdown);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event => {
      if (event.target instanceof Element && event.target.closest("a") !== null) {
        closeDropdown();
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const closeOnLinkClick = t4;
  let t5;
  if ($[5] !== pathname || $[6] !== searchParameters) {
    t5 = lang => {
      const langRegex = /^\/(de|en|fr)\//;
      let newPath;
      const searchParametersString = searchParameters.toString();
      const searchParameterPrefixed = searchParametersString === "" ? "" : `?${searchParametersString}`;
      if (langRegex.test(pathname)) {
        newPath = pathname.replace(langRegex, `/${lang}/`) + searchParameterPrefixed;
      } else {
        const path = pathname.replace(/\/(de|en|fr)\/?$/, "");
        const cleanPath = path.startsWith("//") ? path.slice(1) : path;
        const pathWithoutLeadingSlash = cleanPath.replace(/^\//, "");
        newPath = `/${lang}/${pathWithoutLeadingSlash}${searchParameterPrefixed}`;
      }
      if (newPath.endsWith("/") && newPath.length > 1) {
        newPath = newPath.slice(0, -1);
      }
      globalThis.location.assign(newPath);
    };
    $[5] = pathname;
    $[6] = searchParameters;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleLanguageChange = t5;
  let t6;
  if ($[8] !== actionURL || $[9] !== locale || $[10] !== router || $[11] !== searchQuery) {
    t6 = event_0 => {
      event_0.preventDefault();
      const trimmedQuery = searchQuery.trim();
      if (trimmedQuery !== "") {
        router.push(`/${locale}${actionURL}?q=${encodeURIComponent(trimmedQuery)}`);
      }
    };
    $[8] = actionURL;
    $[9] = locale;
    $[10] = router;
    $[11] = searchQuery;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleSearchSubmit = t6;
  let t7;
  if ($[13] !== isSearchExpanded) {
    t7 = () => {
      if (isSearchExpanded) {
        setIsSearchExpanded(false);
      } else {
        setIsSearchExpanded(true);
        setTimeout(() => {
          searchInputReference.current?.focus();
        }, 50);
      }
    };
    $[13] = isSearchExpanded;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const toggleSearchExpand = t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn("relative min-w-0 flex-1 justify-end-safe overflow-hidden", topLevelRowClassName);
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== openDropdownId) {
    t9 = item_2 => {
      const isOpen = openDropdownId === item_2.id;
      return <TopLevelItem key={item_2.id} item={item_2} isOpen={isOpen} onToggle={() => setOpenDropdownId(isOpen ? undefined : item_2.id)} />;
    };
    $[16] = openDropdownId;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = fittingItems.map(t9);
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("pointer-events-none invisible absolute top-0 left-0", topLevelRowClassName);
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = overflowButtonLabel[locale];
  let t13;
  if ($[19] !== t12) {
    t13 = <span>{t12}</span>;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronDown className="size-4" />;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t13) {
    t15 = <span className={topLevelItemClassName}>{t13}{t14}</span>;
    $[22] = t13;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setIsLangOpen(true);
    t17 = () => setIsLangOpen(false);
    $[24] = t16;
    $[25] = t17;
  } else {
    t16 = $[24];
    t17 = $[25];
  }
  let t18;
  if ($[26] !== isLangOpen) {
    t18 = () => setIsLangOpen(!isLangOpen);
    $[26] = isLangOpen;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Languages className="text-conveniat-green size-3.5" />;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== locale) {
    t20 = <span className="uppercase">{locale}</span>;
    $[29] = locale;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  const t21 = isLangOpen && "rotate-180";
  let t22;
  if ($[31] !== t21) {
    t22 = cn("size-3 text-gray-400 transition-transform", t21);
    $[31] = t21;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== t22) {
    t23 = <ChevronDown className={t22} />;
    $[33] = t22;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  if ($[35] !== t18 || $[36] !== t20 || $[37] !== t23) {
    t24 = <button type="button" onClick={t18} className="hover:border-conveniat-green/40 hover:text-conveniat-green flex h-8 items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50/80 px-2.5 text-xs font-semibold text-gray-700 transition-all duration-200 hover:bg-white">{t19}{t20}{t23}</button>;
    $[35] = t18;
    $[36] = t20;
    $[37] = t23;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== handleLanguageChange || $[40] !== isLangOpen || $[41] !== locale) {
    t25 = isLangOpen && <div className="absolute top-full right-0 z-50 w-32 pt-2"><div className="animate-in fade-in-0 slide-in-from-top-1 rounded-xl border border-gray-200/80 bg-white/95 p-1.5 backdrop-blur-xl duration-150">{languageOptions.map(t26 => {
          const {
            value,
            label
          } = t26;
          return <button key={value} type="button" onClick={() => handleLanguageChange(value)} className={cn("block w-full rounded-lg px-3 py-1.5 text-left text-xs font-semibold transition-colors", locale === value ? "bg-conveniat-green/10 text-conveniat-green font-bold" : "hover:text-conveniat-green text-gray-700 hover:bg-gray-100")}>{label}</button>;
        })}</div></div>;
    $[39] = handleLanguageChange;
    $[40] = isLangOpen;
    $[41] = locale;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== t24 || $[44] !== t25) {
    t26 = <div className="relative" onMouseEnter={t16} onMouseLeave={t17}>{t24}{t25}</div>;
    $[43] = t24;
    $[44] = t25;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  const t27 = isSearchExpanded && "2xl:hidden";
  let t28;
  if ($[46] !== t27) {
    t28 = cn("hover:border-conveniat-green/40 hover:text-conveniat-green flex size-8 items-center justify-center rounded-xl border border-gray-200 bg-gray-50/80 text-gray-600 transition-all duration-200 hover:bg-white", t27);
    $[46] = t27;
    $[47] = t28;
  } else {
    t28 = $[47];
  }
  const t29 = searchLabel[locale];
  const t30 = searchLabel[locale];
  let t31;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Search className="size-3.5 text-gray-500" />;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  if ($[49] !== t28 || $[50] !== t29 || $[51] !== t30 || $[52] !== toggleSearchExpand) {
    t32 = <button type="button" onClick={toggleSearchExpand} className={t28} aria-label={t29} title={t30}>{t31}</button>;
    $[49] = t28;
    $[50] = t29;
    $[51] = t30;
    $[52] = toggleSearchExpand;
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  let t33;
  if ($[54] !== handleSearchSubmit || $[55] !== isSearchExpanded || $[56] !== locale || $[57] !== searchQuery) {
    t33 = isSearchExpanded && <form onSubmit={handleSearchSubmit} className="animate-in fade-in-0 slide-in-from-right-2 absolute top-0 right-0 z-50 flex h-8 items-center 2xl:relative 2xl:top-auto 2xl:right-auto 2xl:z-auto"><div className="relative flex h-8 items-center"><input ref={searchInputReference} type="text" value={searchQuery} onChange={event_1 => setSearchQuery(event_1.target.value)} onBlur={() => {
          if (searchQuery.trim() === "") {
            setIsSearchExpanded(false);
          }
        }} placeholder={searchPlaceholder[locale]} className="h-8 w-56 rounded-xl border border-gray-200 bg-white pr-7 pl-8 text-xs text-gray-800 transition-all duration-200 focus:border-gray-300 focus:ring-0 focus:outline-hidden 2xl:w-64" /><Search className="pointer-events-none absolute left-2.5 size-3.5 text-gray-500" /><button type="button" onClick={() => {
          setSearchQuery("");
          setIsSearchExpanded(false);
        }} className="hover:text-conveniat-green absolute right-2 flex size-4 items-center justify-center text-gray-400"><X className="size-3" /></button></div></form>;
    $[54] = handleSearchSubmit;
    $[55] = isSearchExpanded;
    $[56] = locale;
    $[57] = searchQuery;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  let t34;
  if ($[59] !== t32 || $[60] !== t33) {
    t34 = <div className="relative flex h-8 items-center justify-end">{t32}{t33}</div>;
    $[59] = t32;
    $[60] = t33;
    $[61] = t34;
  } else {
    t34 = $[61];
  }
  let t35;
  if ($[62] !== t26 || $[63] !== t34) {
    t35 = <div className="relative flex h-8 shrink-0 items-center gap-2 border-l border-gray-200/80 pl-3">{t26}{t34}</div>;
    $[62] = t26;
    $[63] = t34;
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  return <div className="hidden min-w-0 flex-1 items-center gap-4 xl:flex"><nav ref={navReference} onClick={closeOnLinkClick} className={t8}>{t10}{overflowItems.length > 0 && <DropdownButton label={overflowButtonLabel[locale]} isOpen={isOverflowOpen} onToggle={() => setOpenDropdownId(isOverflowOpen ? undefined : OVERFLOW_DROPDOWN_ID)} />}<div ref={measureRowReference} aria-hidden={true} className={t11}>{navItems.map(_temp2)}{t15}</div></nav>{(hasActiveSubMenu || isOverflowOpen) && <div ref={flyoutReference} className="animate-in fade-in-0 slide-in-from-top-1 fixed top-16 right-0 left-0 z-50 w-full border-b-2 border-gray-200 bg-white/98 backdrop-blur-2xl transition-all duration-200" onClick={closeOnLinkClick}><div className="w-full px-6 py-8 xl:px-12">{isOverflowOpen && <div className={cn("flex-wrap", topLevelRowClassName, hasActiveSubMenu && "mb-8 border-b border-gray-200 pb-4")}>{overflowItems.map(item_4 => {
            const isOpen_0 = openDropdownId === item_4.id;
            return <TopLevelItem key={item_4.id} item={item_4} isOpen={isOpen_0} onToggle={() => setOpenDropdownId(isOpen_0 ? OVERFLOW_DROPDOWN_ID : item_4.id)} />;
          })}</div>}<div className="flex flex-wrap gap-8 xl:gap-12">{(activeSubMenu ?? []).map(_temp4)}</div></div></div>}{t35}</div>;
};
function _temp(item) {
  return item.isVisible;
}
function _temp2(item_3) {
  return <span key={item_3.id} className={topLevelItemClassName}><span>{item_3.label}</span>{hasSubMenu(item_3) && <ChevronDown className="size-4" />}</span>;
}
function _temp3(subSubItem) {
  if (!subSubItem.isVisible) {
    return;
  }
  if (typeof subSubItem.itemLink === "string" && subSubItem.itemLink !== "") {
    return <LinkComponent key={subSubItem.id} href={subSubItem.itemLink} openInNewTab={subSubItem.openInNewTab} prefetch={true} className="group/item hover:bg-conveniat-green/10 hover:text-conveniat-green flex items-center rounded-xl px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-150"><span className="flex items-center gap-2"><span className="bg-conveniat-green/40 group-hover/item:bg-conveniat-green size-1.5 rounded-full transition-colors" /><span>{subSubItem.label}</span></span></LinkComponent>;
  }
  return <span key={subSubItem.id} className="block px-3 py-1.5 text-xs font-medium text-gray-400">{subSubItem.label}</span>;
}
function _temp4(subItem) {
  if (!subItem.isVisible) {
    return;
  }
  const hasSubSub = Array.isArray(subItem.subMenu) && subItem.subMenu.length > 0;
  return <div key={subItem.id} className="max-w-[280px] min-w-[220px] flex-1">{hasSubSub ? <>{typeof subItem.itemLink === "string" && subItem.itemLink !== "" ? <LinkComponent href={subItem.itemLink} openInNewTab={subItem.openInNewTab} prefetch={true} className="group/head text-conveniat-green hover:text-conveniat-green/80 border-conveniat-green/20 font-heading mb-3 flex h-8 items-center border-b pb-1 text-xs font-extrabold tracking-wider uppercase transition-colors"><span className="truncate">{subItem.label}</span></LinkComponent> : <div className="text-conveniat-green border-conveniat-green/20 font-heading mb-3 flex h-8 items-center border-b pb-1 text-xs font-extrabold tracking-wider uppercase"><span className="truncate">{subItem.label}</span></div>}<div className="flex flex-col space-y-1">{subItem.subMenu?.map(_temp3)}</div></> : <SubItemWithoutChildren subItem={subItem} />}</div>;
}